import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as venditoriApi from '@/api/venditori'
import { toast } from 'sonner'
import { fetchAllPaginatedResults } from '@/utils/pagination'

/**
 * Hook per lista venditori con filtri e ricerca.
 */
export const useVenditori = (params = {}) => {
  return useQuery({
    queryKey: ['venditori', params],
    queryFn: () => venditoriApi.getVenditori(params).then(res => res.data),
    // I totali cambiano quando si conferma, modifica o annulla un ordine.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export const useVenditoriAttivi = () => {
  return useQuery({
    queryKey: ['venditori', 'attivi', 'all-pages'],
    queryFn: () => fetchAllPaginatedResults(
      (params) => venditoriApi.getVenditori(params),
      { attivo: true }
    ),
    staleTime: 5 * 60 * 1000,
  })
}

export const useVenditoriTutti = () => useQuery({
  queryKey: ['venditori', 'tutti', 'all-pages'],
  queryFn: () => fetchAllPaginatedResults((params) => venditoriApi.getVenditori(params)),
  staleTime: 5 * 60 * 1000,
})

/**
 * Hook per dettaglio singolo venditore.
 */
export const useVenditore = (id) => {
  return useQuery({
    queryKey: ['venditore', id],
    queryFn: () => venditoriApi.getVenditore(id).then(res => res.data),
    enabled: !!id,
  })
}

/**
 * Hook per ricerca venditori (autocomplete).
 */
export const useSearchVenditori = (search) => {
  return useQuery({
    queryKey: ['venditori', 'search', search],
    queryFn: () => venditoriApi.searchVenditori(search).then(res => res.data),
    enabled: search?.length >= 2,
  })
}

/**
 * Hook per creazione venditore.
 */
export const useCreateVenditore = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: venditoriApi.createVenditore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venditori'] })
      toast.success('Venditore creato con successo')
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Errore durante la creazione'
      toast.error(message)
    },
  })
}

/**
 * Hook per aggiornamento venditore.
 */
export const useUpdateVenditore = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => venditoriApi.updateVenditore(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venditori'] })
      queryClient.invalidateQueries({ queryKey: ['venditore'] })
      toast.success('Venditore aggiornato con successo')
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Errore durante l\'aggiornamento'
      toast.error(message)
    },
  })
}

/**
 * Hook per eliminazione venditore.
 */
export const useDeleteVenditore = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: venditoriApi.deleteVenditore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venditori'] })
      toast.success('Venditore eliminato con successo')
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Errore durante l\'eliminazione'
      toast.error(message)
    },
  })
}
